import { Group, MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useMemo } from 'react';
import type { Noop } from 'react-hook-form';
import { type Control, useWatch } from 'react-hook-form';

import type {
  EstimationModelItemGroupResponse,
  EstimationModelResponse,
} from '@/api/schema/data-contracts';
import AssumptionIcon from '@/assets/assum-icon-noparam.svg?react';
import ConversationIcon from '@/assets/conversation-icon.svg?react';
import DetailIcon from '@/assets/detail-icon-noparam.svg?react';
import PlusCircleIcon from '@/assets/plus-circle-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import { DebugHighlighter } from '@/components/atoms/DebugHighlighter';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { NumericFormField } from '@/components/atoms/NumericFormField';
import { SwitchFormField } from '@/components/atoms/SwitchFormField';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';
import { sortCostTypes } from '@/utils/sorting';

import type {
  CommentModel,
  CostEstimationItemsEditFormModel,
  CostEstimationItemsTableRecord,
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
  EstimationItemIsIncludedActionCallback,
  EstimationItemIsIncludedActionPromiseCallback,
} from '../model';
import { mapFormModelItemWithOriginalData } from './formModelItemWithOriginalDataMapper';
import { costEstimationItemsTableMessageDefinitions } from './messages';
import type { FormModelItemWithOriginalData } from './model';

export interface CostEstimationItemsTableProps {
  control: Control<CostEstimationItemsEditFormModel>;
  group?: EstimationModelItemGroupResponse;
  estimationItems: CostEstimationItemsTableRecord[];
  comments?: CommentModel[];
  estimationModel: EstimationModelResponse;
  onCommentsClicked: EstimationItemActionCallback;
  onDetailsClicked: EstimationItemActionCallback;
  onAssumptionsClicked: EstimationItemActionCallback;
  onIncludeClicked?: EstimationItemIsIncludedActionPromiseCallback;
  onIncludeChanged?: EstimationItemIsIncludedActionCallback;
  onAddEstimationItemClicked?: EstimationGroupActionCallback;
  onCostFieldBlur?: Noop;
  showButtons?: boolean;
  isReadOnly: boolean;
  searchText: string;
  hiddenCostTypeColumns?: string[];
}

const CostEstimationItemsTable: React.FC<CostEstimationItemsTableProps> = ({
  control,
  group,
  estimationItems = [],
  estimationModel,
  onAssumptionsClicked,
  onCommentsClicked,
  onDetailsClicked,
  onIncludeClicked,
  onIncludeChanged,
  onAddEstimationItemClicked,
  onCostFieldBlur,
  showButtons,
  isReadOnly,
  searchText,
  hiddenCostTypeColumns = [],
}: CostEstimationItemsTableProps) => {
  const messages = useMessages(costEstimationItemsTableMessageDefinitions);
  const formModel = useWatch({ control });

  if (!formModel.estimationItems) {
    throw new Error('Form model estimationItems is undefined');
  }

  const formModelItemsWithOriginalData = useMemo(
    () =>
      mapFormModelItemWithOriginalData(
        formModel as CostEstimationItemsEditFormModel,
        estimationItems,
        group,
      ),
    [formModel, estimationItems, group],
  );

  const costsColumns: DataTableColumn<FormModelItemWithOriginalData>[] = useMemo(
    () =>
      sortCostTypes(estimationModel.costTypes, estimationModel.costTypeGroups)
        .filter((costType) => !hiddenCostTypeColumns?.includes(costType.id))
        .map((costType) => {
          return {
            accessor: `costType_${costType.id}`,
            title: costType.name,
            noWrap: true,
            textAlign: 'right',
            width: 200,
            titleClassName: 'whitespace-break-spaces',
            render: (item) => {
              if (!item.formModelItem.isIncluded) {
                return undefined;
              }

              const formModelItemsIndex = item.formModelItemIndex;

              if (!item.formModelItem.estimationItemCosts) {
                throw new Error('Estimation item costs in form model cannot be undefined here.');
              }

              const estimationItemCostIndex = item.formModelItem.estimationItemCosts.findIndex(
                (itemCost) => itemCost.costTypeId === costType.id,
              );

              // If cost type is not found in the form model, return 0
              if (estimationItemCostIndex === -1) {
                return <FormattedNumber value={0} />;
              }

              if (isReadOnly || item.originalData?.areFilledSubItemsEstimationItemCosts) {
                const costValue =
                  item.formModelItem.estimationItemCosts[estimationItemCostIndex]?.price ?? 0;
                return <FormattedNumber value={costValue} />;
              }

              const formField = (
                <NumericFormField
                  control={control}
                  fieldPath={`estimationItems.${formModelItemsIndex}.estimationItemCosts.${estimationItemCostIndex}.price`}
                  onBlur={onCostFieldBlur}
                />
              );
              const estimationItemCostValue =
                item.formModelItem.estimationItemCosts[estimationItemCostIndex]?.price ?? 0;
              const savedValue = item.originalData?.estimationItemCosts.find(
                (x) => x.costTypeId === costType.id,
              )?.price;
              const isSaved = savedValue === estimationItemCostValue;
              return (
                <DebugHighlighter precondition={!isSaved} tooltip={`Saved value: ${savedValue}`}>
                  {formField}
                </DebugHighlighter>
              );
            },
            footer: (
              <FormattedNumber
                value={formModelItemsWithOriginalData
                  ?.filter(
                    (x) => x.formModelItem.groupId === group?.id && x.formModelItem.isIncluded,
                  ) // TODO: why is group nullable?
                  .map(
                    (x) =>
                      x.formModelItem.estimationItemCosts?.find(
                        (estimationItemCost) => estimationItemCost.costTypeId === costType.id,
                      )?.price ?? 0,
                  )
                  .reduce((prev, current) => prev + current, 0)}
              />
            ),
          };
        }),
    [
      estimationModel.costTypes,
      estimationModel.costTypeGroups,
      hiddenCostTypeColumns,
      formModelItemsWithOriginalData,
      group,
      control,
      isReadOnly,
      onCostFieldBlur,
    ],
  );

  const columns: DataTableColumn<FormModelItemWithOriginalData>[] = useMemo(
    () => [
      {
        accessor: 'hierarchicalOrderReference',
        width: 50,
        title: messages.referenceNumber(),
        noWrap: true,
        sortable: false,
        render: (item) => item.originalData?.reference,
      },
      {
        accessor: 'name',
        width: 300,
        title: group?.name ?? messages.emptyTableGroupName(),
        noWrap: false,
        sortable: false,
        render: (item) => {
          if (!formModel.estimationItems) {
            throw new Error('estimation items are undefined');
          }
          const formModelItem = item.formModelItem;

          const omitReason = !formModelItem.isIncluded ? formModelItem.omitReason : undefined;
          if (omitReason) {
            return (
              <>
                {item.formModelItem.name}
                <p className='text-xs'>{stripHtml(omitReason)}</p>
              </>
            );
          }
          return item.formModelItem.name;
        },
        footer:
          onAddEstimationItemClicked && !isReadOnly ? (
            <Button
              variant='primary'
              size='sm'
              IconComponentLeft={PlusCircleIcon}
              onClick={() => onAddEstimationItemClicked(group?.id)}
            >
              {messages.addEstimationItemButton()}
            </Button>
          ) : undefined,
      },
      {
        accessor: 'buttons',
        title: '',
        noWrap: true,
        width: 0,
        cellsClassName: '!max-w-none',
        render: (item: FormModelItemWithOriginalData) => (
          <Group gap={4} justify='left' wrap='nowrap'>
            <Button
              variant='secondary'
              outlined
              onClick={() => {
                onDetailsClicked(item.formModelItem.id);
              }}
              disabled={!item.originalData}
              key='detail'
              size='sm'
              data-tooltip-top=''
              data-title={messages.detailsButton()}
            >
              <DetailIcon className='-mx-1 h-5 w-5 shrink-0' />
              <span className='sr-only'>{messages.detailsButton()}</span>
            </Button>
            <Button
              variant='secondary'
              outlined
              onClick={() => {
                onAssumptionsClicked(item.formModelItem.id);
              }}
              disabled={!item.originalData}
              key='cost-assumptions'
              size='sm'
              data-count={item.originalData?.assumptionsCount ?? 0}
              data-title={messages.assumptionsButton()}
              data-tooltip-top=''
            >
              <AssumptionIcon className='-mx-1 h-5 w-5 shrink-0' />
              <span className='sr-only'>{messages.assumptionsButton()}</span>
            </Button>
            <Button
              type='button'
              outlined
              variant='secondary'
              size='sm'
              onClick={() => {
                onCommentsClicked(item.formModelItem.id);
              }}
              disabled={!item.originalData}
              data-title={messages.commentsButton()}
              data-tooltip-top=''
            >
              <span className='sr-only'>{messages.commentsButton()}</span>
              <ConversationIcon className='-mx-1 h-5 w-5 shrink-0' />
            </Button>
          </Group>
        ),
        hidden: !showButtons,
      },
      {
        accessor: 'estimate_placeholder',
        title: '',
        width: 150,
        render: () => null,
      },
      ...costsColumns,

      {
        accessor: 'costSum',
        title: messages.subtotal(),
        noWrap: true,
        width: 200,
        textAlign: 'right',
        titleClassName: 'whitespace-break-spaces',
        render: (item: FormModelItemWithOriginalData) => {
          if (!item.formModelItem.isIncluded) {
            return undefined;
          }

          const formModelItem = item.formModelItem;

          const itemCostsSum = _sum(
            formModelItem.estimationItemCosts?.map(
              (estimationItemCost) => estimationItemCost?.price ?? 0,
            ) ?? [],
          );
          return <FormattedNumber value={itemCostsSum} />;
        },
        footer: formModelItemsWithOriginalData.length ? (
          <FormattedNumber
            value={_sum(
              formModelItemsWithOriginalData
                .filter((x) => {
                  if (!x.formModelItem.isIncluded) {
                    return false;
                  }
                  return x.formModelItem.isIncluded && x.formModelItem.groupId === group?.id;
                })
                .map((x) =>
                  _sum(
                    x.formModelItem.estimationItemCosts?.map(
                      (estimationItemCost) => estimationItemCost?.price ?? 0,
                    ) ?? [],
                  ),
                ),
            )}
          />
        ) : undefined,
      },
      {
        accessor: 'isIncluded',
        title: messages.included(),
        noWrap: true,
        hidden: isReadOnly || !onIncludeClicked,
        render: (item) => {
          if (!onIncludeClicked) {
            throw new Error(
              'OnIncludeClicked is undefined. It should never happen as whole column is hidden in that case.',
            );
          }

          return (
            <SwitchFormField
              control={control}
              fieldPath={`estimationItems.${item.formModelItemIndex}.isIncluded`}
              onBeforeChange={(checked) => onIncludeClicked(item.formModelItem.id, checked)}
              onChange={(checked) => onIncludeChanged?.(item.formModelItem.id, checked)}
            />
          );
        },
      },
    ],
    [
      costsColumns,
      group,
      messages,
      formModel,
      onAddEstimationItemClicked,
      isReadOnly,
      onDetailsClicked,
      onAssumptionsClicked,
      onCommentsClicked,
      showButtons,
      onIncludeClicked,
      onIncludeChanged,
      control,
      formModelItemsWithOriginalData,
    ],
  );

  return (
    <MantineProvider>
      <div className='relative'>
        <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
        <DataTable<FormModelItemWithOriginalData>
          key={`direct-cost-table-${costsColumns.length}-${hiddenCostTypeColumns.join(',')}`}
          highlightOnHover
          withTableBorder
          className='table-hover rounded-xl p-2 whitespace-nowrap'
          records={formModelItemsWithOriginalData}
          rowStyle={(record) =>
            !record.formModelItem.name.toLowerCase().includes(searchText.toLowerCase())
              ? { visibility: 'collapse' }
              : undefined
          }
          columns={columns}
          emptyState={estimationItems.length === 0 ? <div>{messages.emptyTableText()}</div> : null}
        />
      </div>
    </MantineProvider>
  );
};

export default CostEstimationItemsTable;
